import { Injectable } from '@angular/core';
import { config } from '../../config';

@Injectable({
  providedIn: 'root'
})
export class LoggingService {
  private logLevel: LogLevel;
  private readonly defaultLogLevel: LogLevel = LogLevel.Warning;

  constructor() {
    const logLevelConfigValue: string = (config.logging || LogLevel[this.defaultLogLevel]).toString();

    if (!config.logging) {
      console.warn(`No log level specific in config.js, defaulting to '${LogLevel[this.defaultLogLevel]}'.`);
    }

    let logLevel = this.getLogLevel(logLevelConfigValue);

    if (logLevel === undefined) {
      console.warn(`Failed to parse log level from the provided vale of '${config.logging || ''}' in config.js, defaulting to '${LogLevel[this.defaultLogLevel]}'.`);
      logLevel = this.defaultLogLevel;
    }

    this.logLevel = logLevel;
  }

  /** Log a debug-level message and any associated objects to the browser console. */
  debug(message: any, ...objects: any[]) {
    if (this.logLevel > LogLevel.Debug) {
      return;
    }

    if (typeof message === 'string' && objects && objects.length > 0) {
      console.log(message, ...objects);
    } else {
      console.log(message);
    }
  }

  /** Log an info-level message and any associated objects to the browser console. */
  info(message: any, ...objects: any[]) {
    if (this.logLevel > LogLevel.Info) {
      return;
    }

    if (typeof message === 'string' && objects && objects.length > 0) {
      console.log(message, ...objects);
    } else {
      console.log(message);
    }
  }

  /** Log a warning-level message and any associated objects to the browser console. */
  warning(message: any, ...objects: any[]) {
    if (this.logLevel > LogLevel.Warning) {
      return;
    }

    if (typeof message === 'string' && objects && objects.length > 0) {
      console.warn(message, ...objects);
    } else {
      console.warn(message);
    }
  }

  /** Log an error-level message and any associated objects to the browser console. */
  error(message: any, ...objects: any[]) {
    if (this.logLevel > LogLevel.Error) {
      return;
    }

    if (typeof message === 'string' && objects && objects.length > 0) {
      console.error(message, ...objects);
    } else {
      console.error(message);
    }
  }

  /** Gets a LogLevel value from a configuration string value. */
  private getLogLevel(logLevel: string): LogLevel {
    // Trim spaces from the ends to account for user error
    logLevel = logLevel.trim();
    // Capitalises the first letter and makes the rest lowercase, to match the enum value
    logLevel = logLevel.charAt(0).toUpperCase() + logLevel.slice(1).toLowerCase();

    // Cast to the keys of the enum and get an enum value if possible
    let logLevelEnumKeyValue = logLevel as keyof typeof LogLevel;
    return LogLevel[logLevelEnumKeyValue];
  }
}

export enum LogLevel {
  /** Allows logging of all messages. */
  Debug = 1,
  /** Allows logging of info, warning and error messages. */
  Info = 2,
  /** Allows logging of warning and error messages. */
  Warning = 3,
  /** Allows logging of error messages only. */
  Error = 4,
  /** Disables all logging. */
  None = 999,
}
